'use client';

import { cn } from '@bells/core/lib/utils';

import {
  fileUploadingText,
  fileUploadingTitle,
  fileUploadText,
  fileUploadTitle,
} from '@bells/i18n';
import type { AllPossibleRestErrors } from '@bells/schema/models/data';
import type { MediaResponsePagination } from '@bells/schema/models/media';
import { Loader2 } from 'lucide-react';
import React, { useCallback, useState } from 'react';
import { useDropzone, type Accept } from 'react-dropzone';
import { useToast } from '../../utils/use-toast';
import type { QueryClient } from '@tanstack/react-query';
import type { ClientArgs } from '@ts-rest/core';
import type {
  TsRestReactQueryHooksContainer,
  TsRestReactQueryClient,
} from '@ts-rest/react-query/v5';
import mediaContract from '@bells/api/media/backend';
import { generateClient } from '@bells/core/rest/client';

const mediaClient = generateClient(mediaContract);
export interface FileUploadProps {
  onUploadComplete?: (url: string) => void;
  mimeTypes?: string[];
  accept?: Accept;
  systemRestClient: TsRestReactQueryHooksContainer<
    typeof mediaContract,
    ClientArgs
  > & {
    useQueryClient: () => TsRestReactQueryClient<
      typeof mediaContract,
      ClientArgs
    >;
    initQueryClient: (
      queryClient: QueryClient
    ) => TsRestReactQueryClient<typeof mediaContract, ClientArgs>;
  };
}

const FileUpload: React.FC<FileUploadProps> = ({
  mimeTypes = [
    'image/png',
    'image/jpeg',
    'image/webp',
    'video/mp4',
    'image/svg',
  ],
  accept,
  systemRestClient,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [, setSelectedImage] = useState<File | null>(null);
  const { toast } = useToast();
  const tsrQueryClient = systemRestClient.useQueryClient();

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files?.length) {
      const image = event.target.files[0];
      if (image) {
        setSelectedImage(image);
        handleImageUpload(image);
      }
    }
  };

  const handleImageUpload = async (image: File) => {
    if (!image) {
      return;
    }
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('file', image);
      const data = await mediaClient.upload({ body: formData });
      if (data.status === 200) {
        const item = data.body;

        tsrQueryClient.setQueriesData<{
          pages: { body: MediaResponsePagination }[];
          pageParams: { cursor: string | undefined }[];
        }>({ queryKey: [`media-pool`, ...mimeTypes] }, (data) => {
          const hackTheData = data?.pages ?? [];

          if (hackTheData.length > 0) {
            hackTheData[0]?.body.items.unshift(item);
          }
          return { pages: hackTheData, pageParams: data?.pageParams ?? [] };
        });
      } else {
        const error: AllPossibleRestErrors = data.body as any;

        toast({
          title: 'Uppps...',
          variant: 'destructive',
          description: error.message,
        });
      }

      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error('Error uploading image:', error);
    }
  };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const image = acceptedFiles[0];

      if (image) {
        setSelectedImage(image);
        handleImageUpload(image);
      }
    }
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    noClick: true,
    noKeyboard: true,
    accept,
  });

  return (
    <div className='h-full space-y-3'>
      <div {...getRootProps()} className='h-full'>
        <label
          htmlFor='dropzone-file'
          className='dark:hover:bg-bray-800 visually-hidden-focusable relative flex h-full w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-4 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-700 dark:hover:border-gray-500 dark:hover:bg-gray-600'
        >
          {loading && (
            <div className='max-w-md text-center'>
              <Loader2
                className={cn('h-4 w-4 animate-spin text-center', 'mr-2')}
              />
              <p className='text-sm font-semibold'>{fileUploadingTitle()}</p>
              <p className='text-xs text-gray-400'>{fileUploadingText()}</p>
            </div>
          )}

          {!loading && (
            <div className='text-center'>
              <p className='mt-2 text-sm text-gray-500 dark:text-gray-400'>
                <span className='font-semibold'>{fileUploadTitle()}</span>
              </p>
              <p className='text-xs text-gray-400 dark:text-gray-400'>
                {fileUploadText()}
              </p>
            </div>
          )}
        </label>

        <input
          {...getInputProps()}
          id='dropzone-file'
          accept={(mimeTypes ?? []).join(', ')}
          type='file'
          className='hidden'
          disabled={loading}
          onChange={handleImageChange}
        />
      </div>
    </div>
  );
};

export { FileUpload };
